"use client";

import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert } from "@/components/ui/alert";
import { AlertCircle, RefreshCw, PlusCircle, Inbox } from "lucide-react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";

interface TimelineStateProps {
  className?: string;
}

interface LoadingStateProps extends TimelineStateProps {
  isLoading: boolean;
  message?: string;
}

interface ErrorStateProps extends TimelineStateProps {
  isRefetching: boolean;
  onRefresh: () => void;
  title?: string;
  description?: string;
}

interface EmptyStateProps extends TimelineStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
  title?: string;
  description?: string;
  hideLogAction?: boolean;
}

export const TimelineLoadingState = ({
  className,
  isLoading,
  message = "Loading enhanced timeline...",
}: LoadingStateProps) => {
  const { openModal } = useGlobalModalStack();

  const handleOpenModal = useCallback(() => openModal(MODAL_IDS.messages.logActivity), [openModal]);

  return (
    <div className={`flex h-64 flex-col items-center justify-center space-y-4 ${className}`}>
      <LoadingSpinner />
      <div className="text-center">
        <p className="text-gray-600">{message}</p>
      </div>
      <Button onClick={handleOpenModal} size="sm" disabled={isLoading}>
        <PlusCircle className="h-4 w-4 mr-2" />
        Log Activity
      </Button>
    </div>
  );
};

export const TimelineErrorState = ({
  className,
  isRefetching,
  onRefresh,
  title = "Failed to load timeline",
  description = "Check your connection and try again",
}: ErrorStateProps) => {
  const { openModal } = useGlobalModalStack();

  const handleOpenModal = useCallback(() => openModal(MODAL_IDS.messages.logActivity), [openModal]);

  return (
    <div className={`flex h-64 flex-col items-center justify-center space-y-4 ${className}`}>
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <span className="text-lg font-medium">{title}</span>
      </div>
      <Alert className="max-w-md">
        <p className="text-sm">{description}</p>
      </Alert>
      <div className="flex gap-2">
        <Button onClick={onRefresh} variant="outline" size="sm" disabled={isRefetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? "animate-spin" : ""}`} />
          Try Again
        </Button>
        <Button onClick={handleOpenModal} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Log Activity
        </Button>
      </div>
    </div>
  );
};

export const TimelineEmptyState = ({
  className,
  hasFilters,
  onClearFilters,
  title,
  description,
  hideLogAction = false,
}: EmptyStateProps) => {
  const { openModal } = useGlobalModalStack();

  const handleOpenModal = useCallback(() => openModal(MODAL_IDS.messages.logActivity), [openModal]);

  return (
    <div className={`flex h-64 flex-col items-center justify-center space-y-4 ${className}`}>
      <Inbox className="h-12 w-12 text-gray-400" />
      <div className="text-center">
        <p className="text-gray-600">{title || (hasFilters ? "No activities match your filters" : "No activities yet")}</p>
        <p className="text-sm text-gray-500">
          {description || (hasFilters ? "Try adjusting your filters" : "Start by logging an activity")}
        </p>
      </div>
      <div className="flex gap-2">
        {hasFilters && (
          <Button onClick={onClearFilters} variant="outline" size="sm">
            Clear Filters
          </Button>
        )}
        {!hideLogAction && (
          <Button onClick={handleOpenModal} size="sm">
            <PlusCircle className="h-4 w-4 mr-2" />
            Log Activity
          </Button>
        )}
      </div>
    </div>
  );
};
